import React, { useEffect, useState, MouseEventHandler, useMemo } from "react";
import { useFetchApi } from "../../hooks/useFetchApi";
import Loader from "../../components/Loader";
import Button from "../../components/Button";
import H4 from "../../components/Typography/H4";
import Badge from "../../components/Badge";
import AddEditModal from "./AddEditModal";
import DeleteModal from "./DeleteModal";
import BulkUploadModal from "./BulkUploadModal";
import ButtonWithDropdown, {
  OptionTypes,
} from "../../components/ButtonWithDropdown";
import { Table } from "../../components/table";
import type { ColumnsType } from "antd/es/table";

// Define the Sector type
interface Sector {
  id: number;
  sector_name: string;
  sector_meta?: string;
  status: boolean;
}

const Sector: React.FC = (): React.ReactElement => {
  const [selectedData, setEditData] = useState<any>({});
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [openModal, toggleModal] = useState<any>({
    newEdit: false,
    delete: false,
    bulkUpload: false,
  });
  const {
    execute,
    loading,
    response: sectors,
  } = useFetchApi(`/api/sectors`, {}, "GET");

  const opts: Array<OptionTypes> = [
    { label: "Add New", id: "newEdit" },
    { label: "Bulk Upload", id: "bulkUpload" },
  ];

  const toggle = (e: any, sector?: any) => {
    const type = e.target.id;

    if (sector && Object.keys(sector).length) {
      setEditData(sector);
    }
    if (type) {
      toggleModal({ ...openModal, [type]: !openModal[type] });
    }
    if (openModal[type]) {
      setEditData({});
    }
  };

  const filteredSectors = useMemo(() => {
    if (!sectors || !searchTerm.trim()) {
      return sectors || [];
    }
    return sectors.filter((sector: Sector) =>
      sector.sector_name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [sectors, searchTerm]);

  const clearSearch = () => {
    setSearchTerm("");
  };

  const columns: ColumnsType<Sector> = [
    {
      title: "ID #",
      dataIndex: "id",
      key: "id",
      align: "left",
      width: 80,
    },
    {
      title: "Sector Name",
      dataIndex: "sector_name",
      key: "sector_name",
      align: "left",
    },
    {
      title: "Meta (Optional)",
      dataIndex: "sector_meta",
      key: "sector_meta",
      align: "left",
      render: (meta: string) => meta || " - ",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "left",
      render: (status: boolean) => (
        <Badge status={status ? "success" : "error"}>
          {status ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      width: 300,
      render: (_, sector: Sector) => (
        <div className="flex gap-2 justify-center">
          <Button
            fontSize="small"
            variant="primary"
            id="newEdit"
            onClick={(e: MouseEventHandler<HTMLButtonElement>) =>
              toggle(e, sector)
            }
          >
            Edit
          </Button>
          <Button
            fontSize="small"
            variant="error"
            id="delete"
            onClick={(e: MouseEventHandler<HTMLButtonElement>) =>
              toggle(e, sector)
            }
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  useEffect(() => {
    execute();
  }, []);

  return (
    <div className="flex flex-col w-full p-8 bg-white min-h-full">
      <Loader show={loading} />
      <AddEditModal
        data={selectedData}
        status={openModal.newEdit}
        onClose={toggle}
        onSuccess={execute}
      />
      <DeleteModal
        id="delete"
        data={selectedData}
        status={openModal.delete}
        onClose={toggle}
        onSuccess={execute}
      />
      <BulkUploadModal
        data={selectedData}
        status={openModal.bulkUpload}
        onClose={toggle}
        onSuccess={execute}
      />

      <div className="flex justify-between mb-8 text-white items-center relative z-1">
        <H4>Sectors</H4>
        <div className="relative z-10">
          <ButtonWithDropdown
            fontSize="small"
            options={opts}
            onClick={toggle}
            variant="primary"
          >
            Add New Sector
          </ButtonWithDropdown>
        </div>
      </div>

      <div className="mb-4 flex items-center gap-3 relative z-0">
        <div className="relative flex-1 max-w-md">
          <input
            type="text"
            placeholder="Search sector name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        {searchTerm && (
          <button
            onClick={clearSearch}
            className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Clear
          </button>
        )}
        {searchTerm && (
          <span className="text-sm text-gray-600">
            Found {filteredSectors.length} of {sectors?.length || 0}
          </span>
        )}
      </div>

      <div className="relative z-0">
        <Table<Sector>
          columns={columns}
          dataSource={filteredSectors}
          rowKey="id"
          loading={loading}
          pagination={{
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
          rowHoverable={true}
          bordered={true}
          size="middle"
          nullText="No data available. Add new +"
          className="shadow-sm"
        />
      </div>
    </div>
  );
};

export default Sector;
